import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgpInput } from 'ng-primitives/input';

/**
 * A single-line text field.
 *
 * NgpInput sits on the native <input> and publishes interaction state
 * (data-hover, data-focus, data-focus-visible, data-disabled) for the
 * design system to style against. Value handling stays ours, because
 * the primitive deliberately does not own it — this speaks
 * ControlValueAccessor so ngModel and reactive forms both work.
 *
 * `label` and `error` are optional and purely additive: unset, the
 * component renders exactly the bare input it always has, so no
 * existing call site changes shape.
 *
 * `error`'s hint text and red-ring styling are unconditional and always
 * correct. What it deliberately does NOT do is drive aria-invalid or
 * aria-describedby: NgpInput's own ngpFormControl composable owns both
 * of those attributes itself — driven by the real NgControl's validity
 * — through an internal effect that runs after template bindings
 * settle, so it always wins over a competing binding of our own.
 * Feeding `error` into real control validity via a Validator only
 * works if ng-primitives can find that NgControl by querying this
 * component's own template content (NgpFormField, its intended
 * integration point, does exactly that) — but the NgControl here lives
 * on this component's *host* element (wherever a caller writes
 * `[(ngModel)]="x"` on <hh-input>), one level above this template, so
 * there is nothing inside it for that query to find. Wiring it up for
 * real would mean moving ngModel onto the inner <input> itself, which
 * breaks every existing call site. Left as a known gap rather than a
 * binding that only sometimes works.
 */
@Component({
  selector: 'hh-input',
  standalone: true,
  imports: [NgpInput],
  template: `
    <div class="hh-field">
      @if (label()) {
        <label class="hh-field__label" [for]="inputId">{{ label() }}</label>
      }
      <input
        ngpInput
        class="hh-input"
        [id]="inputId"
        [class.hh-input--error]="!!error()"
        [type]="type()"
        [placeholder]="placeholder()"
        [name]="name()"
        [required]="required()"
        [disabled]="disabled()"
        [value]="value()"
        (input)="handleInput($event)"
        (blur)="onTouched()"
      />
      @if (error()) {
        <span class="hh-field__hint hh-field__hint--error">{{ error() }}</span>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HhInputComponent),
      multi: true,
    },
  ],
  styleUrl: './hh-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhInputComponent implements ControlValueAccessor {
  private static nextId = 0;
  protected readonly inputId = `hh-input-${HhInputComponent.nextId++}`;

  readonly placeholder = input('');
  readonly type = input('text');
  readonly name = input('');
  readonly required = input(false, { transform: booleanAttribute });

  /** Optional label above the field. Unset renders no label at all. */
  readonly label = input('');

  /** Optional error hint below the field (see the class doc comment). */
  readonly error = input('');

  /** Written by forms via setDisabledState, not bound by callers. */
  protected readonly disabled = signal(false);
  protected readonly value = signal('');

  private onChange: (val: string) => void = () => {};
  protected onTouched: () => void = () => {};

  protected handleInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChange(next);
  }

  writeValue(val: string): void {
    this.value.set(val ?? '');
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
